import { IUser } from '@ViewModels';
import { css } from 'aphrodite';
import * as React from 'react';
import { useUserSession } from '../../../../../../models/hooks/appStateHooks';
import { Button } from '../../../../../../web/components-v2/Button';
import { ComboboxContent, ComboboxInput, ComboboxList } from '../../../../../../web/components-v2/Combobox';
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '../../../../../../web/components-v2/Select';
import {
	ManagerCombobox,
	ManagerComboboxStatus,
	UserComboboxAvatarItem,
} from '../../../../../../web/components/UserCombobox';
import { bs } from '../../../../../../web/styles/styles';
import { CompanyTagsSelect } from '../../../../../components/leadView/components/CompanyTagsSelect';
import { DealStageMultiSelect } from '../../../../../components/leadView/components/DealStageMultiSelect';
import { OfficeLocationMultiSelect } from '../../../../../components/leadView/components/OfficeLocationMultiSelect';
import { OutcomeMultiSelectDropdown } from '../../../../../components/leadView/components/OutcomeMultiSelectDropdown';
import { QuotaTeamMultiSelect } from '../../../../../components/leadView/components/QuotaTeamMultiSelect';
import { TeamMultiSelect } from '../../../../../components/leadView/components/TeamMultiSelect';
import { UserMultiSelect } from '../../../../../components/leadView/components/UserMultiSelect';
import { CUSTOMER_TYPE_OPTIONS, CustomerType } from '../../../../Demos/constants';
import type { ILeadViewFilter } from '../../../../../entities/LeadView/useLeadView';
import { styleSheet } from './styles';

interface CallFiltersProps {
	filter: ILeadViewFilter;
	onFilterChange: (filter: ILeadViewFilter) => void;
	onClearFilters: () => void;
}

const dateTypeOptions = [
	{ label: 'Call Date', value: 'call-date' },
	{ label: 'Demo Date', value: 'demo-date' },
];

const recordingChannelsOptions = [
	{ label: 'All Channels', value: 'all' },
	{ label: 'Rep Only', value: '1' },
	{ label: 'All Parties', value: '2' },
];

export const CallFilters: React.FC<CallFiltersProps> = ({ filter, onFilterChange, onClearFilters }) => {
	const id = React.useId();
	const userSession = useUserSession();
	const isSalesCoffeeAccount = userSession?.account?.isLevitateSalesCoffeeAccount;
	// Generic handler for filter changes
	const handleFilterChange = React.useCallback(
		(key: keyof ILeadViewFilter) => {
			return (item: any) => {
				if (!item) {
					const newFilter = { ...filter };
					delete newFilter[key];
					onFilterChange(newFilter);
					return;
				}
				onFilterChange({ ...filter, [key]: item });
			};
		},
		[filter, onFilterChange]
	);

	// Special handler for team multi-select
	const handleTeamFilterChange = React.useCallback(
		(options: any[]) => {
			if (!options) {
				const newFilter = { ...filter };
				delete newFilter.teams;
				onFilterChange(newFilter);
				return;
			}

			// Extract team names from checked options (excluding "All SDRs" helper)
			const selectedTeams = options
				.filter(option => option.checked && option.id !== 'all-sdrs')
				.map(option => option.dataContext || option.name);

			onFilterChange({ ...filter, teams: selectedTeams });
		},
		[filter, onFilterChange]
	);

	// Special handler for user multi-select with backward compatibility
	const handleUserFilterChange = React.useCallback(
		(users: IUser[]) => {
			if (!users || users.length === 0) {
				const newFilter = { ...filter };
				delete newFilter.user;
				delete newFilter.users;
				onFilterChange(newFilter);
				return;
			}
			// Set both user (for backward compatibility) and users (for multi-select)
			// user field gets the first selected user for backward compatibility
			onFilterChange({
				...filter,
				user: users[0], // First user for backward compatibility
				users, // All users for multi-select
			});
		},
		[filter, onFilterChange]
	);

	// Deal stage multi-select handler
	const handleDealStageFilterChange = React.useCallback(
		(stages: string[]) => {
			if (!stages || stages.length === 0) {
				const newFilter = { ...filter };
				delete newFilter.dealStages;
				onFilterChange(newFilter);
				return;
			}
			onFilterChange({ ...filter, dealStages: stages });
		},
		[filter, onFilterChange]
	);

	// Quota team multi-select handler
	const handleQuotaTeamFilterChange = React.useCallback(
		(values: string[]) => {
			if (!values || values.length === 0) {
				const newFilter = { ...filter };
				delete newFilter.quotaTeams;
				onFilterChange(newFilter);
				return;
			}
			onFilterChange({ ...filter, quotaTeams: values });
		},
		[filter, onFilterChange]
	);

	// Office location multi-select handler
	const handleOfficeLocationFilterChange = React.useCallback(
		(values: string[]) => {
			if (!values || values.length === 0) {
				const newFilter = { ...filter };
				delete newFilter.officeLocations;
				onFilterChange(newFilter);
				return;
			}
			onFilterChange({ ...filter, officeLocations: values });
		},
		[filter, onFilterChange]
	);

	// Call duration range handler (converts minutes to milliseconds)
	const handleCallDurationChange = React.useCallback(
		(field: 'start' | 'end', minutes: string) => {
			if (minutes === '') {
				// Clearing the field
				const cleared = filter.callDuration ? { ...filter.callDuration } : {};
				delete cleared[field];
				// If both fields are empty, remove the filter entirely
				if (cleared.start == null && cleared.end == null) {
					const newFilter = { ...filter };
					delete newFilter.callDuration;
					onFilterChange(newFilter);
				} else {
					onFilterChange({ ...filter, callDuration: cleared });
				}
				return;
			}
			const parsed = parseInt(minutes, 10);
			if (isNaN(parsed) || parsed < 0) return;
			const ms = parsed * 60 * 1000;
			const currentDuration = filter.callDuration || {};
			const newDuration = { ...currentDuration, [field]: ms };
			// Prevent inverted ranges
			if (newDuration.start != null && newDuration.end != null && newDuration.start > newDuration.end) {
				return;
			}
			onFilterChange({
				...filter,
				callDuration: newDuration,
			});
		},
		[filter, onFilterChange]
	);

	const formatDurationMinutes = (ms: number | undefined) => {
		if (ms == null) return '';
		return String(Math.round(ms / 60000));
	};

	// Date range presets
	const dateRangePresets = [
		{ label: 'Today', value: 'today' },
		{ label: 'Yesterday', value: 'yesterday' },
		{ label: 'Last 7 Days', value: 'last7days' },
		{ label: 'Last 30 Days', value: 'last30days' },
		{ label: 'This Month', value: 'thismonth' },
		{ label: 'Last Month', value: 'lastmonth' },
		{ label: 'Custom', value: 'custom' },
	];

	const handleDateRangePresetChange = React.useCallback(
		(preset: string) => {
			if (preset === 'custom') return; // Don't change dates for custom option

			const now = new Date();
			const todayDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
			let startDate: Date;
			let endDate: Date = new Date(todayDate.getTime() + 24 * 60 * 60 * 1000 - 1); // End of today

			switch (preset) {
				case 'today':
					startDate = todayDate;
					break;
				case 'yesterday':
					startDate = new Date(todayDate.getTime() - 24 * 60 * 60 * 1000);
					endDate = new Date(todayDate.getTime() - 1);
					break;
				case 'last7days':
					startDate = new Date(todayDate.getTime() - 7 * 24 * 60 * 60 * 1000);
					endDate = new Date(todayDate.getTime() + 24 * 60 * 60 * 1000 - 1);
					break;
				case 'last30days':
					startDate = new Date(todayDate.getTime() - 30 * 24 * 60 * 60 * 1000);
					endDate = new Date(todayDate.getTime() + 24 * 60 * 60 * 1000 - 1);
					break;
				case 'thismonth':
					startDate = new Date(now.getFullYear(), now.getMonth(), 1);
					endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999);
					break;
				case 'lastmonth':
					startDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
					endDate = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59, 999);
					break;
				default:
					return;
			}

			onFilterChange({
				...filter,
				date: { start: startDate, end: endDate },
			});
		},
		[filter, onFilterChange]
	);

	const handleDateRangeChange = React.useCallback(
		(field: 'start' | 'end', date: string) => {
			const parsed = new Date(date);
			if (isNaN(parsed.getTime())) return;

			const currentDate = filter.date || { start: new Date(), end: new Date() };
			const newDate = {
				...currentDate,
				[field]: parsed,
			};
			onFilterChange({
				...filter,
				date: newDate,
			});
		},
		[filter, onFilterChange]
	);

	// Determine current preset selection based on the date range
	const getCurrentPreset = React.useCallback(() => {
		if (!filter.date) return 'today';

		const { start, end } = filter.date;
		const now = new Date();
		const todayDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
		const yesterday = new Date(todayDate.getTime() - 24 * 60 * 60 * 1000);

		// Check if it matches today
		if (start.getTime() >= todayDate.getTime() && end.getTime() <= todayDate.getTime() + 24 * 60 * 60 * 1000) {
			return 'today';
		}

		// Check if it matches yesterday
		if (start.getTime() >= yesterday.getTime() && end.getTime() <= todayDate.getTime()) {
			return 'yesterday';
		}

		// Check if it matches last 7 days (approximately)
		const sevenDaysAgo = new Date(todayDate.getTime() - 7 * 24 * 60 * 60 * 1000);
		if (
			Math.abs(start.getTime() - sevenDaysAgo.getTime()) < 24 * 60 * 60 * 1000 &&
			end.getTime() > todayDate.getTime()
		) {
			return 'last7days';
		}

		// Check if it matches last 30 days (approximately)
		const thirtyDaysAgo = new Date(todayDate.getTime() - 30 * 24 * 60 * 60 * 1000);
		if (
			Math.abs(start.getTime() - thirtyDaysAgo.getTime()) < 24 * 60 * 60 * 1000 &&
			end.getTime() > todayDate.getTime()
		) {
			return 'last30days';
		}

		// Default to custom
		return 'custom';
	}, [filter.date]);

	const today = new Date().toISOString().split('T')[0];
	const formatDateValue = (date: Date | undefined) => {
		if (!date || isNaN(date.getTime())) return today;
		return date.toISOString().split('T')[0];
	};

	return (
		<div className={css(bs.flex, bs.flexCol, bs.gap2)}>
			<div className={css(styleSheet.clearFilterGroup)}>
				<Button variant='secondary' size='sm' onClick={onClearFilters}>
					✕ Clear All Filters
				</Button>
			</div>
			<div className={`${css(styleSheet.container)} callFilters`}>
				<div className={css(styleSheet.filterGroup)}>
					<label className={css(styleSheet.label)} htmlFor='date-type'>
						Date Type
					</label>
					<Select
						items={dateTypeOptions}
						value={filter.dateType || 'call-date'}
						onValueChange={handleFilterChange('dateType')}
					>
						<SelectTrigger id='date-type' styles={[bs.wFull]}>
							<SelectValue />
						</SelectTrigger>
						<SelectContent>
							{dateTypeOptions.map(option => (
								<SelectItem key={option.value} value={option.value}>
									{option.label}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				</div>

				{/* Date Range Helper */}
				<div className={css(styleSheet.filterGroup)}>
					<label className={css(styleSheet.label)} htmlFor='quick-select'>
						Quick Select
					</label>
					<Select items={dateRangePresets} value={getCurrentPreset()} onValueChange={handleDateRangePresetChange}>
						<SelectTrigger id='quick-select' styles={[bs.wFull]}>
							<SelectValue />
						</SelectTrigger>
						<SelectContent>
							{dateRangePresets.map(preset => (
								<SelectItem key={preset.value} value={preset.value}>
									{preset.label}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				</div>

				{/* Date Range */}
				<div className={css(styleSheet.filterGroup)}>
					<label className={css(styleSheet.label)}>Date Range</label>
					<div className={css(styleSheet.dateRange)}>
						<input
							type='date'
							value={formatDateValue(filter.date?.start)}
							onChange={e => handleDateRangeChange('start', e.target.value)}
							className={css(styleSheet.dateInput)}
						/>
						<span className={css(styleSheet.dateSeparator)}>to</span>
						<input
							type='date'
							value={formatDateValue(filter.date?.end)}
							onChange={e => handleDateRangeChange('end', e.target.value)}
							className={css(styleSheet.dateInput)}
						/>
					</div>
				</div>

				{/* Recording Channels Filter */}
				<div className={css(styleSheet.filterGroup)}>
					<label className={css(styleSheet.label)}>Recording Channels</label>
					<div className={css(styleSheet.selectWrapper)}>
						<Select
							items={recordingChannelsOptions}
							value={filter.recordingChannels || 'all'}
							onValueChange={value => {
								if (value === 'all') {
									handleFilterChange('recordingChannels')(null);
									return;
								}
								handleFilterChange('recordingChannels')(value);
							}}
						>
							<SelectTrigger styles={[bs.wFull]}>
								<SelectValue />
							</SelectTrigger>
							<SelectContent>
								{recordingChannelsOptions.map(option => (
									<SelectItem key={option.value} value={option.value}>
										{option.label}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>
				</div>

				<UserMultiSelect users={filter.users || (filter.user ? [filter.user] : [])} onChange={handleUserFilterChange} />

				<ManagerCombobox id={filter.manager?.id} key={filter.manager?.id} onValueChange={handleFilterChange('manager')}>
					<div className={css(bs.flex, bs.flexCol, bs.gap1)}>
						<label htmlFor={`${id}-manager-combobox`} className={css(bs.textXs, bs.fontBold)}>
							Manager
						</label>
						<ComboboxInput placeholder='Filter by manager' id={`${id}-manager-combobox`} showClear />
					</div>
					<ComboboxContent>
						<ManagerComboboxStatus />
						<ComboboxList>{(user: IUser) => <UserComboboxAvatarItem key={user.id} user={user} />}</ComboboxList>
					</ComboboxContent>
				</ManagerCombobox>

				{/* Outcome Selector */}
				<div className={css(styleSheet.filterGroup)}>
					<label className={css(styleSheet.label)}>Outcomes</label>
					<div className={css(styleSheet.selectWrapper)}>
						<OutcomeMultiSelectDropdown selectedOutcomes={filter.outcomes} onChange={handleFilterChange('outcomes')} />
					</div>
				</div>

				{/* Team Selector */}
				<div className={css(styleSheet.filterGroup)}>
					<label className={css(styleSheet.label)}>Teams</label>
					<div className={css(styleSheet.selectWrapper)}>
						<TeamMultiSelect onChange={handleTeamFilterChange} selectedTeams={filter.teams || []} />
					</div>
				</div>

				{/* Quota Team Selector — Sales Coffee only */}
				{isSalesCoffeeAccount && (
					<div className={css(styleSheet.filterGroup)}>
						<label className={css(styleSheet.label)}>Quota Team</label>
						<div className={css(styleSheet.selectWrapper)}>
							<QuotaTeamMultiSelect
								onChange={handleQuotaTeamFilterChange}
								selectedQuotaTeams={filter.quotaTeams || []}
							/>
						</div>
					</div>
				)}

				{/* Office Location Selector — Sales Coffee only */}
				{isSalesCoffeeAccount && (
					<div className={css(styleSheet.filterGroup)}>
						<label className={css(styleSheet.label)}>Office Location</label>
						<div className={css(styleSheet.selectWrapper)}>
							<OfficeLocationMultiSelect
								onChange={handleOfficeLocationFilterChange}
								selectedOfficeLocations={filter.officeLocations || []}
							/>
						</div>
					</div>
				)}

				{/* Company Tags Selector */}
				<div className={css(styleSheet.filterGroup)}>
					<label className={css(styleSheet.label)}>Company Tags</label>
					<div className={css(styleSheet.selectWrapper)}>
						<CompanyTagsSelect
							onChange={handleFilterChange('companyTag')}
							className={css(styleSheet.managerSelect)}
							autoCompleteInputWrapperStyles={[styleSheet.managerSelectAutoCompleteInputWrapper]}
						/>
					</div>
				</div>

				{/* Customer Type Filter */}
				{isSalesCoffeeAccount && (
					<div className={css(styleSheet.filterGroup)}>
						<label className={css(styleSheet.label)} htmlFor={`${id}-customer-type`}>
							Customer Type
						</label>
						<Select
							value={filter.customerType ?? CustomerType.All}
							onValueChange={value => handleFilterChange('customerType')(value === CustomerType.All ? null : value)}
							items={CUSTOMER_TYPE_OPTIONS}
						>
							<SelectTrigger id={`${id}-customer-type`} styles={[bs.wFull]}>
								<SelectValue />
							</SelectTrigger>
							<SelectContent>
								{CUSTOMER_TYPE_OPTIONS.map(option => (
									<SelectItem key={option.value} value={option.value}>
										{option.label}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>
				)}

				{/* Deal Stage Multi-Select - Sales Coffee only */}
				{isSalesCoffeeAccount && (
					<div className={css(styleSheet.filterGroup)}>
						<label className={css(styleSheet.label)}>Deal Stage</label>
						<div className={css(styleSheet.selectWrapper)}>
							<DealStageMultiSelect selectedStages={filter.dealStages || []} onChange={handleDealStageFilterChange} />
						</div>
					</div>
				)}

				{/* Call Duration Range */}
				<div className={css(styleSheet.filterGroup)}>
					<label className={css(styleSheet.label)}>Call Duration (minutes)</label>
					<div className={css(styleSheet.dateRange)}>
						<input
							type='number'
							min='0'
							step='1'
							placeholder='Min'
							value={formatDurationMinutes(filter.callDuration?.start)}
							onChange={e => handleCallDurationChange('start', e.target.value)}
							className={css(styleSheet.dateInput)}
						/>
						<span className={css(styleSheet.dateSeparator)}>to</span>
						<input
							type='number'
							min='0'
							step='1'
							placeholder='Max'
							value={formatDurationMinutes(filter.callDuration?.end)}
							onChange={e => handleCallDurationChange('end', e.target.value)}
							className={css(styleSheet.dateInput)}
						/>
					</div>
				</div>
			</div>
		</div>
	);
};
